import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Draw as DrawIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { mailKontenApi, authApi, benutzerApi } from '../services/api';
import { zertifikateService, ZertifikatInfo } from '../services/zertifikateService';
import { SignatureCanvas } from '../components/SignatureCanvas';
import type { MailKonto } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

const BenutzerEinstellungen: React.FC = () => {
  const { user } = useAuth();
  const [mailKonten, setMailKonten] = useState<MailKonto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Personal settings state
  const [standardOrt, setStandardOrt] = useState<string>('');
  const [savingPersonal, setSavingPersonal] = useState(false);

  // Paperless-NGX settings state
  const [paperlessUrl, setPaperlessUrl] = useState<string>('');
  const [paperlessUsername, setPaperlessUsername] = useState<string>('');
  const [paperlessToken, setPaperlessToken] = useState<string>('');
  const [savingPaperless, setSavingPaperless] = useState(false);

  // Signature settings state
  const [unterschriftUrl, setUnterschriftUrl] = useState<string | null>(null);
  const [loadingUnterschrift, setLoadingUnterschrift] = useState(false);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Digital Certificate settings state
  const [zertifikate, setZertifikate] = useState<ZertifikatInfo[]>([]);
  const [loadingZertifikate, setLoadingZertifikate] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certName, setCertName] = useState('');
  const [certBeschreibung, setCertBeschreibung] = useState('');
  const [certPasswort, setCertPasswort] = useState('');
  const certFileInputRef = React.useRef<HTMLInputElement>(null);

  // Mail-Konto Dialog States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    kontoName: '',
    emailAdresse: '',
    imapServer: '',
    imapPort: 993,
    imapSsl: true,
    imapBenutzer: '',
    imapPasswort: '',
    smtpServer: '',
    smtpPort: 587,
    smtpSsl: true,
    smtpBenutzer: '',
    smtpPasswort: '',
    aktiv: true,
    standardKonto: false,
  });

  useEffect(() => {
    loadData();
    loadUnterschrift();
    loadZertifikate();
  }, []);

  useEffect(() => {
    // Load user's standard location and Paperless settings
    if (user?.standardOrt) {
      setStandardOrt(user.standardOrt);
    }
    if (user?.paperlessUrl) {
      setPaperlessUrl(user.paperlessUrl);
    }
    if (user?.paperlessUsername) {
      setPaperlessUsername(user.paperlessUsername);
    }
    if (user?.paperlessToken) {
      setPaperlessToken(user.paperlessToken);
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const kontenResponse = await mailKontenApi.getAll();
      setMailKonten(kontenResponse.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(
        err.response?.data?.message || 'Fehler beim Laden der Einstellungen'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSavePersonalSettings = async () => {
    if (!user) return;

    try {
      setSavingPersonal(true);
      await authApi.updateProfile({ standardOrt });
      alert('Persönliche Einstellungen erfolgreich gespeichert!');
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern der Einstellungen');
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleSavePaperlessSettings = async () => {
    if (!user) return;

    try {
      setSavingPaperless(true);
      await authApi.updateProfile({
        paperlessUrl: paperlessUrl.trim() || undefined,
        paperlessUsername: paperlessUsername.trim() || undefined,
        paperlessToken: paperlessToken.trim() || undefined
      });
      alert('Paperless-NGX Einstellungen erfolgreich gespeichert!');
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern der Paperless-Einstellungen');
    } finally {
      setSavingPaperless(false);
    }
  };

  const handleToggleKontoActive = async (konto: MailKonto) => {
    try {
      await mailKontenApi.toggleActive(konto.kontoID, !konto.istAktiv);
      setMailKonten(
        mailKonten.map((k) =>
          k.kontoID === konto.kontoID ? { ...k, istAktiv: !k.istAktiv } : k
        )
      );
    } catch (err: any) {
      alert('Fehler beim Ändern des Konto-Status');
    }
  };

  const handleSetStandardKonto = async (kontoId: number) => {
    try {
      await mailKontenApi.setStandard(kontoId);
      setMailKonten(
        mailKonten.map((k) => ({
          ...k,
          istStandard: k.kontoID === kontoId,
        }))
      );
    } catch (err: any) {
      alert('Fehler beim Setzen des Standard-Kontos');
    }
  };

  const handleDeleteKonto = async (kontoId: number) => {
    if (!window.confirm('Mail-Konto wirklich löschen?')) return;

    try {
      await mailKontenApi.delete(kontoId);
      setMailKonten(mailKonten.filter((k) => k.kontoID !== kontoId));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Löschen des Kontos');
    }
  };

  const handleOpenCreateDialog = () => {
    // Reset form
    setFormData({
      kontoName: '',
      emailAdresse: '',
      imapServer: '',
      imapPort: 993,
      imapSsl: true,
      imapBenutzer: '',
      imapPasswort: '',
      smtpServer: '',
      smtpPort: 587,
      smtpSsl: true,
      smtpBenutzer: '',
      smtpPasswort: '',
      aktiv: true,
      standardKonto: false,
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateKonto = async () => {
    // Validierung
    if (!formData.kontoName.trim() || !formData.emailAdresse.trim() ||
        !formData.imapServer.trim() || !formData.smtpServer.trim()) {
      alert('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    try {
      setCreating(true);
      await mailKontenApi.create(formData);
      alert('Mail-Konto erfolgreich erstellt!');
      setCreateDialogOpen(false);
      await loadData(); // Daten neu laden
    } catch (err: any) {
      console.error('Fehler beim Erstellen:', err);
      alert('Fehler beim Erstellen des Mail-Kontos: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  const loadUnterschrift = async () => {
    try {
      setLoadingUnterschrift(true);
      const response = await benutzerApi.getUnterschrift();
      const url = URL.createObjectURL(response.data);
      setUnterschriftUrl(url);
    } catch (err: any) {
      // 404 ist OK - bedeutet einfach keine Unterschrift vorhanden
      if (err.response?.status !== 404) {
        console.error('Fehler beim Laden der Unterschrift:', err);
      }
    } finally {
      setLoadingUnterschrift(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    try {
      setLoadingUnterschrift(true);
      await benutzerApi.uploadUnterschrift(file);
      alert('Unterschrift erfolgreich hochgeladen!');
      await loadUnterschrift();
    } catch (err: any) {
      console.error('Fehler beim Upload:', err);
      alert('Fehler beim Hochladen der Unterschrift');
    } finally {
      setLoadingUnterschrift(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveSignature = async (signatureDataUrl: string) => {
    try {
      setLoadingUnterschrift(true);

      // Convert data URL to blob
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'signature.png', { type: 'image/png' });

      await benutzerApi.uploadUnterschrift(file);
      alert('Unterschrift erfolgreich gespeichert!');
      setShowSignatureCanvas(false);
      await loadUnterschrift();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern der Unterschrift');
    } finally {
      setLoadingUnterschrift(false);
    }
  };

  const handleDeleteUnterschrift = async () => {
    if (!window.confirm('Unterschrift wirklich löschen?')) return;

    try {
      setLoadingUnterschrift(true);
      await benutzerApi.deleteUnterschrift();
      setUnterschriftUrl(null);
      alert('Unterschrift erfolgreich gelöscht!');
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      alert('Fehler beim Löschen der Unterschrift');
    } finally {
      setLoadingUnterschrift(false);
    }
  };

  // Certificate management functions
  const loadZertifikate = async () => {
    try {
      setLoadingZertifikate(true);
      const certs = await zertifikateService.getMeineZertifikate();
      setZertifikate(certs);
    } catch (err: any) {
      console.error('Fehler beim Laden der Zertifikate:', err);
    } finally {
      setLoadingZertifikate(false);
    }
  };

  const handleOpenUploadDialog = () => {
    setCertFile(null);
    setCertName('');
    setCertBeschreibung('');
    setCertPasswort('');
    setUploadDialogOpen(true);
  };

  const handleCertFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCertFile(file);
      // Set default name from filename
      if (!certName) {
        setCertName(file.name.replace(/\.(pfx|p12)$/i, ''));
      }
    }
  };

  const handleUploadZertifikat = async () => {
    if (!certFile || !certPasswort) {
      alert('Bitte wählen Sie eine Datei und geben Sie das Passwort ein.');
      return;
    }

    try {
      setUploading(true);
      const result = await zertifikateService.uploadZertifikat(
        certFile,
        certPasswort,
        certName || undefined,
        certBeschreibung || undefined
      );

      if (result.success) {
        alert(`Zertifikat erfolgreich hochgeladen!\nSubject: ${result.subject}\nGültig bis: ${result.gueltigBis ? new Date(result.gueltigBis).toLocaleDateString('de-DE') : '-'}`);
        setUploadDialogOpen(false);
        await loadZertifikate();
      } else {
        alert('Fehler beim Hochladen: ' + result.errorMessage);
      }
    } catch (err: any) {
      console.error('Fehler beim Upload:', err);
      alert('Fehler beim Hochladen: ' + (err.response?.data?.errorMessage || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefaultZertifikat = async (zertifikatId: number) => {
    try {
      await zertifikateService.setDefault(zertifikatId);
      await loadZertifikate();
    } catch (err: any) {
      alert('Fehler beim Setzen als Standard: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteZertifikat = async (zertifikatId: number) => {
    if (!window.confirm('Zertifikat wirklich löschen?')) return;

    try {
      await zertifikateService.deleteZertifikat(zertifikatId);
      await loadZertifikate();
      alert('Zertifikat erfolgreich gelöscht!');
    } catch (err: any) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Meine Einstellungen</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={loadData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Personal Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Persönliche Einstellungen
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Diese Einstellungen gelten für Ihre persönlichen Dokumente und E-Mails.
          Der Standard-Ort wird beim Erstellen von Dokumenten für den Platzhalter <code>{'{{ort}}'}</code> verwendet.
        </Alert>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Standard-Ort"
            value={standardOrt}
            onChange={(e) => setStandardOrt(e.target.value)}
            fullWidth
            placeholder="z.B. Zürich, München, Wien"
            helperText="Dieser Ort wird automatisch in Dokumenten eingefügt, wenn Sie den Platzhalter {{ort}} verwenden"
          />

          <Box>
            <Button
              variant="contained"
              startIcon={savingPersonal ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSavePersonalSettings}
              disabled={savingPersonal}
            >
              {savingPersonal ? 'Speichere...' : 'Einstellungen speichern'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Signature Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Meine Unterschrift
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Hinterlegen Sie Ihre Unterschrift als Bild oder zeichnen Sie diese direkt.
          Die Unterschrift kann in Dokumenten über den Platzhalter {'{{Unterschrift}}'} eingefügt werden.
        </Alert>

        {loadingUnterschrift ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : showSignatureCanvas ? (
          <SignatureCanvas
            onSave={handleSaveSignature}
            onCancel={() => setShowSignatureCanvas(false)}
          />
        ) : (
          <Box>
            {unterschriftUrl && (
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  display: 'inline-block',
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Aktuelle Unterschrift:
                </Typography>
                <img
                  src={unterschriftUrl}
                  alt="Unterschrift"
                  style={{ maxWidth: '400px', maxHeight: '150px', display: 'block' }}
                />
              </Box>
            )}

            <Box display="flex" gap={2} flexWrap="wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingUnterschrift}
              >
                {unterschriftUrl ? 'Neue Datei hochladen' : 'Datei hochladen'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DrawIcon />}
                onClick={() => setShowSignatureCanvas(true)}
                disabled={loadingUnterschrift}
              >
                {unterschriftUrl ? 'Neu zeichnen' : 'Unterschrift zeichnen'}
              </Button>
              {unterschriftUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteUnterschrift}
                  disabled={loadingUnterschrift}
                >
                  Löschen
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Digital Certificates Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Digitale Zertifikate
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenUploadDialog}
          >
            Zertifikat hochladen
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Laden Sie hier Ihre digitalen Zertifikate (PFX/P12-Dateien) hoch, um PDF-Dokumente kryptografisch zu signieren.
          Die digitale Signatur stellt die Authentizität und Integrität Ihrer Dokumente sicher.
        </Alert>

        {loadingZertifikate ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : zertifikate.length === 0 ? (
          <Box textAlign="center" py={3}>
            <VerifiedUserIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary" gutterBottom>
              Keine Zertifikate vorhanden
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleOpenUploadDialog}
              sx={{ mt: 1 }}
            >
              Erstes Zertifikat hochladen
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Gültig bis</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zertifikate.map((cert) => (
                  <TableRow key={cert.zertifikatID}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <VerifiedUserIcon fontSize="small" color={cert.istGueltig ? 'success' : 'error'} />
                        {cert.name}
                        {cert.istStandard && (
                          <Chip label="Standard" size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cert.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {cert.gueltigBis ? new Date(cert.gueltigBis).toLocaleDateString('de-DE') : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cert.istGueltig ? 'Gültig' : 'Abgelaufen'}
                        size="small"
                        color={cert.istGueltig ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip title={cert.istStandard ? 'Bereits Standard' : 'Als Standard setzen'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSetDefaultZertifikat(cert.zertifikatID)}
                              disabled={cert.istStandard}
                            >
                              {cert.istStandard ? (
                                <StarIcon fontSize="small" color="primary" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteZertifikat(cert.zertifikatID)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Paperless-NGX Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Paperless-NGX Integration
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Konfigurieren Sie Ihre Paperless-NGX Instanz, um Dokumente und E-Mails direkt zu archivieren.
          Sie benötigen die URL Ihrer Paperless-Instanz, Ihren Benutzernamen und ein API-Token.
        </Alert>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Paperless URL"
            value={paperlessUrl}
            onChange={(e) => setPaperlessUrl(e.target.value)}
            fullWidth
            placeholder="z.B. http://192.168.114.72:8000"
            helperText="Die vollständige URL zu Ihrer Paperless-NGX Instanz (inkl. http:// oder https://)"
          />

          <TextField
            label="Benutzername"
            value={paperlessUsername}
            onChange={(e) => setPaperlessUsername(e.target.value)}
            fullWidth
            placeholder="Ihr Paperless-Benutzername"
            helperText="Optional: Ihr Benutzername für Paperless-NGX"
          />

          <TextField
            label="API Token"
            value={paperlessToken}
            onChange={(e) => setPaperlessToken(e.target.value)}
            fullWidth
            type="password"
            placeholder="Ihr Paperless API Token"
            helperText="Sie finden Ihr API-Token in den Paperless-Einstellungen unter 'Mein Profil'"
          />

          <Box>
            <Button
              variant="contained"
              startIcon={savingPaperless ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSavePaperlessSettings}
              disabled={savingPaperless}
            >
              {savingPaperless ? 'Speichere...' : 'Paperless-Einstellungen speichern'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Meine Mail-Konten
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Neues Mail-Konto
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Hier können Sie Ihre persönlichen Mail-Konten verwalten. Beim E-Mail-Abruf werden alle aktiven Konten abgefragt.
        </Alert>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>IMAP Server</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mailKonten.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box py={3}>
                      <Typography color="textSecondary" gutterBottom>
                        Keine Mail-Konten konfiguriert
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                        sx={{ mt: 2 }}
                      >
                        Erstes Mail-Konto erstellen
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                mailKonten.map((konto) => (
                  <TableRow key={konto.kontoID}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {konto.kontoName}
                        {konto.istStandard && (
                          <Chip
                            label="Standard"
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{konto.emailAdresse}</TableCell>
                    <TableCell>
                      {konto.imapServer}:{konto.imapPort}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={konto.istAktiv ? 'Aktiv' : 'Inaktiv'}
                        color={konto.istAktiv ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip
                          title={
                            konto.istStandard
                              ? 'Bereits Standard'
                              : 'Als Standard setzen'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleSetStandardKonto(konto.kontoID)
                              }
                              disabled={konto.istStandard}
                            >
                              {konto.istStandard ? (
                                <StarIcon fontSize="small" color="primary" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            konto.istAktiv
                              ? 'Deaktivieren'
                              : 'Aktivieren'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleKontoActive(konto)}
                          >
                            {konto.istAktiv ? (
                              <ToggleOnIcon
                                fontSize="small"
                                color="success"
                              />
                            ) : (
                              <ToggleOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteKonto(konto.kontoID)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Mail-Konto Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Neues Mail-Konto erstellen</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Konto-Name *"
              value={formData.kontoName}
              onChange={(e) => setFormData({ ...formData, kontoName: e.target.value })}
              fullWidth
              placeholder="z.B. Mein Gmail-Konto"
            />

            <TextField
              label="E-Mail-Adresse *"
              type="email"
              value={formData.emailAdresse}
              onChange={(e) => setFormData({ ...formData, emailAdresse: e.target.value })}
              fullWidth
              placeholder="z.B. meine.email@example.com"
            />

            <Typography variant="subtitle2" color="primary" mt={2}>
              IMAP-Einstellungen (Empfang)
            </Typography>

            <Box display="flex" gap={2}>
              <TextField
                label="IMAP Server *"
                value={formData.imapServer}
                onChange={(e) => setFormData({ ...formData, imapServer: e.target.value })}
                fullWidth
                placeholder="z.B. imap.gmail.com"
              />
              <TextField
                label="Port"
                type="number"
                value={formData.imapPort}
                onChange={(e) => setFormData({ ...formData, imapPort: parseInt(e.target.value) || 993 })}
                sx={{ width: '150px' }}
              />
            </Box>

            <TextField
              label="IMAP Benutzername *"
              value={formData.imapBenutzer}
              onChange={(e) => setFormData({ ...formData, imapBenutzer: e.target.value })}
              fullWidth
              placeholder="Meist identisch mit E-Mail-Adresse"
            />

            <TextField
              label="IMAP Passwort *"
              type="password"
              value={formData.imapPasswort}
              onChange={(e) => setFormData({ ...formData, imapPasswort: e.target.value })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.imapSsl}
                  onChange={(e) => setFormData({ ...formData, imapSsl: e.target.checked })}
                />
              }
              label="SSL/TLS verwenden (empfohlen)"
            />

            <Typography variant="subtitle2" color="primary" mt={2}>
              SMTP-Einstellungen (Versand)
            </Typography>

            <Box display="flex" gap={2}>
              <TextField
                label="SMTP Server *"
                value={formData.smtpServer}
                onChange={(e) => setFormData({ ...formData, smtpServer: e.target.value })}
                fullWidth
                placeholder="z.B. smtp.gmail.com"
              />
              <TextField
                label="Port"
                type="number"
                value={formData.smtpPort}
                onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                sx={{ width: '150px' }}
              />
            </Box>

            <TextField
              label="SMTP Benutzername *"
              value={formData.smtpBenutzer}
              onChange={(e) => setFormData({ ...formData, smtpBenutzer: e.target.value })}
              fullWidth
              placeholder="Meist identisch mit E-Mail-Adresse"
            />

            <TextField
              label="SMTP Passwort *"
              type="password"
              value={formData.smtpPasswort}
              onChange={(e) => setFormData({ ...formData, smtpPasswort: e.target.value })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.smtpSsl}
                  onChange={(e) => setFormData({ ...formData, smtpSsl: e.target.checked })}
                />
              }
              label="SSL/TLS verwenden (empfohlen)"
            />

            <Box display="flex" gap={2} mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.aktiv}
                    onChange={(e) => setFormData({ ...formData, aktiv: e.target.checked })}
                  />
                }
                label="Konto aktivieren"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.standardKonto}
                    onChange={(e) => setFormData({ ...formData, standardKonto: e.target.checked })}
                  />
                }
                label="Als Standard-Konto setzen"
              />
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              Häufige Einstellungen:
              <br />
              • Gmail: IMAP: imap.gmail.com:993, SMTP: smtp.gmail.com:587
              <br />
              • Outlook: IMAP: outlook.office365.com:993, SMTP: smtp.office365.com:587
              <br />
              • Yahoo: IMAP: imap.mail.yahoo.com:993, SMTP: smtp.mail.yahoo.com:587
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateKonto}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {creating ? 'Erstelle...' : 'Mail-Konto erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Certificate Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            Zertifikat hochladen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Alert severity="info">
              Laden Sie ein PFX/P12-Zertifikat hoch. Dieses wird verschlüsselt gespeichert
              und kann zum digitalen Signieren von PDF-Dokumenten verwendet werden.
            </Alert>

            <Box>
              <input
                ref={certFileInputRef}
                type="file"
                accept=".pfx,.p12"
                onChange={handleCertFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => certFileInputRef.current?.click()}
                fullWidth
              >
                {certFile ? certFile.name : 'PFX/P12-Datei auswählen'}
              </Button>
            </Box>

            <TextField
              label="Zertifikats-Passwort *"
              type="password"
              value={certPasswort}
              onChange={(e) => setCertPasswort(e.target.value)}
              fullWidth
              required
              helperText="Das Passwort zum Öffnen der PFX/P12-Datei"
            />

            <TextField
              label="Name"
              value={certName}
              onChange={(e) => setCertName(e.target.value)}
              fullWidth
              placeholder="z.B. Mein Signatur-Zertifikat"
              helperText="Ein Anzeigename für das Zertifikat (optional)"
            />

            <TextField
              label="Beschreibung"
              value={certBeschreibung}
              onChange={(e) => setCertBeschreibung(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optionale Beschreibung..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleUploadZertifikat}
            disabled={uploading || !certFile || !certPasswort}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Lade hoch...' : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenutzerEinstellungen;
